import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { api, apiGet, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { Save, ShieldCheck, User2, MapPin, Wallet, Building2, Briefcase, GraduationCap, Upload, FileText, Trash2, Image as ImageIcon, FileCheck2, Download, AlertCircle, Sparkles, Stamp } from "lucide-react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

// Field sections — each field is mapped to its profile key + label.
const SECTIONS = [
    { title: "Identity", icon: User2, fields: [
        { k: "legal_first_name", label: "Legal first name", type: "text" },
        { k: "legal_last_name",  label: "Legal last name",  type: "text" },
        { k: "preferred_name",   label: "Preferred name",   type: "text" },
        { k: "date_of_birth",    label: "Date of birth",    type: "date" },
        { k: "ssn_last4",        label: "SSN (last 4)",     type: "text", maxLength: 4, placeholder: "••••" },
        { k: "phone",            label: "Phone",            type: "tel" },
        { k: "phone_number",     label: "Mobile for SMS reminders", type: "tel", placeholder: "+1 555 123 4567" },
        { k: "sms_opt_in",       label: "Receive SMS deadline reminders", type: "bool" },
    ]},
    { title: "Address", icon: MapPin, fields: [
        { k: "address_line1",    label: "Street address",   type: "text" },
        { k: "address_line2",    label: "Apt / Suite",       type: "text" },
        { k: "city",             label: "City",              type: "text" },
        { k: "state",            label: "State",             type: "select", options: STATES },
        { k: "zip_code",         label: "ZIP",               type: "text" },
    ]},
    { title: "Household", icon: Wallet, fields: [
        { k: "household_size",   label: "Household size",    type: "number" },
        { k: "marital_status",   label: "Marital status",    type: "select", options: ["", "single", "married", "divorced", "widowed", "partnered"] },
        { k: "dependents",       label: "Dependents",        type: "number" },
        { k: "annual_income",    label: "Annual income",     type: "number" },
        { k: "filing_status",    label: "Tax filing status", type: "select", options: ["", "single", "married_joint", "married_separate", "head_of_household", "qualifying_widow"] },
        { k: "employment_status", label: "Employment",       type: "select", options: ["", "employed", "self_employed", "unemployed", "student", "retired"] },
        { k: "employer",         label: "Employer",          type: "text" },
        { k: "occupation",       label: "Occupation",        type: "text" },
    ]},
    { title: "Education", icon: GraduationCap, fields: [
        { k: "education_level",  label: "Highest education", type: "select", options: ["", "high_school", "associates", "bachelors", "masters", "doctorate", "other"] },
        { k: "currently_enrolled", label: "Currently enrolled", type: "bool" },
        { k: "school_name",      label: "School",            type: "text" },
        { k: "degree_program",   label: "Degree program",    type: "text" },
    ]},
    { title: "Business", icon: Briefcase, fields: [
        { k: "has_business",     label: "Own a business",    type: "bool" },
        { k: "business_name",    label: "Business name",     type: "text" },
        { k: "business_ein",     label: "EIN",               type: "text" },
        { k: "business_type",    label: "Entity type",       type: "select", options: ["", "sole_prop", "llc", "s_corp", "c_corp", "partnership", "nonprofit"] },
        { k: "naics_code",       label: "NAICS code",        type: "text" },
        { k: "num_employees",    label: "# Employees",       type: "number" },
        { k: "annual_revenue",   label: "Annual revenue",    type: "number" },
    ]},
    { title: "Bank (for disbursement)", icon: Building2, fields: [
        { k: "bank_name",         label: "Bank name",         type: "text" },
        { k: "bank_routing",      label: "Routing",           type: "text" },
        { k: "bank_account_last4", label: "Account (last 4)", type: "text", maxLength: 4 },
    ]},
    { title: "Flags", icon: ShieldCheck, fields: [
        { k: "is_us_citizen",            label: "U.S. citizen",                 type: "bool" },
        { k: "is_veteran",               label: "Veteran",                       type: "bool" },
        { k: "is_disabled",              label: "Has a disability",              type: "bool" },
        { k: "is_minority",              label: "Minority / BIPOC",              type: "bool" },
        { k: "is_woman_owned",           label: "Woman-owned (50%+)",            type: "bool" },
        { k: "is_first_time_homebuyer",  label: "First-time homebuyer",          type: "bool" },
    ]},
];

export default function ProfileVault() {
    const { profile, updateProfile } = useApp();
    const [local, setLocal] = useState(profile);
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setLocal(s => ({ ...s, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            // Only send values the user actually touched (strip id / user_id).
            const patch = { ...local };
            delete patch.id; delete patch.user_id; delete patch._id; delete patch.created_at; delete patch.updated_at;
            await updateProfile(patch);
            toast.success("Vault updated — every new packet will use this info.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    const completeness = computeCompleteness(local);

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-5xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Your vault</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Profile details</h1>
                    <p className="mt-3 text-muted-foreground max-w-2xl">This is your single source of truth. Owed AI pulls from it to auto-fill every application. Fill what you can; AI will ask about the rest per-application.</p>
                </div>
                <div className="flex items-center gap-4">
                    <Badge variant="outline" className="bg-secondary/60 border-border rounded-full px-3 py-1" data-testid="vault-completeness">{completeness}% complete</Badge>
                    <Button onClick={save} disabled={saving} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="vault-save-btn">
                        <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save vault"}
                    </Button>
                </div>
            </div>

            <div className="mt-8 space-y-5">
                {SECTIONS.map(({ title, icon: Icon, fields }) => (
                    <Card key={title} className="p-6 border-border" data-testid={`vault-section-${title.toLowerCase()}`}>
                        <div className="flex items-center gap-2 mb-5">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <h2 className="font-serif text-xl">{title}</h2>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {fields.map((f) => (
                                <FieldEditor key={f.k} field={f} value={local[f.k]} onChange={(v) => set(f.k, v)} />
                            ))}
                        </div>
                    </Card>
                ))}

                <VaultDocuments />
            </div>

            <div className="mt-8 flex justify-end">
                <Button onClick={save} disabled={saving} className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-6">
                    <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save all changes"}
                </Button>
            </div>
        </div>
    );
}

function FieldEditor({ field, value, onChange }) {
    if (field.type === "bool") {
        const on = !!value;
        return (
            <button
                type="button"
                onClick={() => onChange(!on)}
                className={`flex items-center justify-between gap-3 p-3 rounded-xl border text-left transition-colors ${on ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground/30"}`}
                data-testid={`vault-field-${field.k}`}
            >
                <span className="text-sm font-medium">{field.label}</span>
                <span className={`inline-flex h-5 w-9 rounded-full transition-colors ${on ? "bg-background/25" : "bg-muted"}`}>
                    <span className={`h-5 w-5 rounded-full bg-background shadow transition-transform ${on ? "translate-x-4 bg-foreground" : ""}`} />
                </span>
            </button>
        );
    }
    if (field.type === "select") {
        return (
            <div>
                <Label className="text-xs">{field.label}</Label>
                <select
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-xl border border-border bg-card text-sm px-3"
                    data-testid={`vault-field-${field.k}`}
                >
                    {field.options.map((o) => <option key={o} value={o}>{o === "" ? "—" : o.replace(/_/g, " ")}</option>)}
                </select>
            </div>
        );
    }
    return (
        <div>
            <Label className="text-xs">{field.label}</Label>
            <Input
                type={field.type}
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                value={value ?? ""}
                onChange={(e) => {
                    const raw = e.target.value;
                    onChange(field.type === "number" ? (raw === "" ? null : Number(raw)) : raw);
                }}
                className="mt-1.5 h-10 rounded-xl"
                data-testid={`vault-field-${field.k}`}
            />
        </div>
    );
}

function computeCompleteness(p) {
    const all = SECTIONS.flatMap(s => s.fields.map(f => f.k));
    const filled = all.filter(k => p[k] !== null && p[k] !== undefined && p[k] !== "" && p[k] !== false);
    return Math.round(100 * filled.length / Math.max(1, all.length));
}

const PRIORITY_KINDS = ["government_id", "proof_of_address"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

function VaultDocuments() {
    const { refreshVaultDocs } = useApp();
    const [kinds, setKinds] = useState([]);
    const [docs, setDocs] = useState([]);
    const [uploadingKind, setUploadingKind] = useState(null);
    const [selectedKind, setSelectedKind] = useState("other");
    const inputRef = useRef(null);

    const refresh = async () => {
        try {
            const [k, d] = await Promise.all([apiGet("/vault/documents/kinds"), apiGet("/vault/documents")]);
            setKinds(k.kinds || []);
            setDocs(d.items || []);
            await refreshVaultDocs();
        } catch { /* ignore */ }
    };
    useEffect(() => { refresh(); }, []);

    const hasKind = (k) => docs.some(d => d.kind === k);

    const openPicker = (kind) => {
        setSelectedKind(kind);
        setUploadingKind(kind);
        setTimeout(() => inputRef.current?.click(), 0);
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking same file
        if (!file) { setUploadingKind(null); return; }
        if (file.size > MAX_SIZE_BYTES) {
            toast.error("File too large — max 5 MB");
            setUploadingKind(null);
            return;
        }
        const fd = new FormData();
        fd.append("kind", uploadingKind || selectedKind || "other");
        fd.append("file", file);
        try {
            await api.post("/vault/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded — it will be attached to every future packet.");
            await refresh();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Upload failed");
        } finally {
            setUploadingKind(null);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this document from your vault?")) return;
        try { await apiDelete(`/vault/documents/${id}`); toast.success("Removed"); await refresh(); }
        catch { toast.error("Couldn't delete"); }
    };

    return (
        <Card className="p-6 border-border" data-testid="vault-section-documents">
            <div className="flex items-center gap-2 mb-2">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <h2 className="font-serif text-xl">Supporting documents</h2>
            </div>
            <p className="text-sm text-muted-foreground">Upload once. Owed auto-attaches them as an appendix to every signed packet. Max 5 MB per file. Images (jpg/png/webp) are embedded; PDFs are concatenated.</p>

            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onFile} className="hidden" data-testid="vault-doc-file-input" />

            {/* Priority slots */}
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {PRIORITY_KINDS.map(k => {
                    const label = kinds.find(x => x.id === k)?.label || k;
                    const mine = docs.filter(d => d.kind === k);
                    const filled = mine.length > 0;
                    return (
                        <div key={k} className={`rounded-xl border p-4 ${filled ? "border-success/40 bg-success/5" : "border-dashed border-border"}`} data-testid={`vault-doc-slot-${k}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        {filled ? <FileCheck2 className="h-4 w-4 text-success" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
                                        <p className="text-sm font-medium">{label}</p>
                                    </div>
                                    {filled && <p className="mt-1 text-xs text-muted-foreground truncate">{mine[0].filename}</p>}
                                </div>
                                <Button size="sm" variant={filled ? "outline" : "default"} onClick={() => openPicker(k)} className="rounded-full h-8 text-xs shrink-0" data-testid={`vault-doc-upload-${k}`}>
                                    <Upload className="h-3.5 w-3.5 mr-1" /> {filled ? "Replace" : "Upload"}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Other upload */}
            <div className="mt-4 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-secondary/40 border border-border">
                <Label className="text-xs text-muted-foreground">Add another:</Label>
                <select value={selectedKind} onChange={(e) => setSelectedKind(e.target.value)} className="h-9 rounded-full border border-border bg-card text-sm px-3" data-testid="vault-doc-kind-select">
                    {kinds.filter(k => !PRIORITY_KINDS.includes(k.id)).map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
                <Button size="sm" onClick={() => openPicker(selectedKind)} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="vault-doc-upload-other-btn">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload file
                </Button>
            </div>

            {/* Affidavits — sworn statements requiring notarization. Always
                rendered in their own highlighted section, displayed side-by-side
                in chronological order (newest first), with the most recent one
                marked as "Start here". */}
            {(() => {
                const AFFIDAVIT_KINDS = ["packet_prop_affidavit", "affidavit"];
                const affidavits = docs
                    .filter(d => AFFIDAVIT_KINDS.includes(d.kind))
                    .slice() // copy
                    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                if (affidavits.length === 0) return null;
                const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                const dlHref = (id) => `${backendBase}/api/vault/documents/${id}/download`;
                return (
                    <div className="mt-6 rounded-2xl border-2 border-accent bg-gradient-to-br from-accent/15 via-card to-card p-5 shadow-elegant" data-testid="vault-affidavits-section">
                        <div className="flex items-center gap-2">
                            <Stamp className="h-4 w-4 text-accent-foreground" />
                            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-accent-foreground">Affidavits · sworn &amp; notarized</p>
                        </div>
                        <h3 className="mt-1 font-serif text-2xl leading-tight">Your affidavits — print &amp; get notarized.</h3>
                        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{affidavits.length} sworn statement{affidavits.length === 1 ? "" : "s"} prepared with your facts. <span className="font-medium text-foreground">Do not sign at home</span> — bring the unsigned document and your photo ID to a notary, sign in front of them, they stamp it.</p>

                        <div className={`mt-5 grid gap-3 ${affidavits.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
                            {affidavits.map((d, i) => {
                                const isNewest = i === 0;
                                const dt = d.created_at ? new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
                                return (
                                    <div
                                        key={d.id}
                                        className={`rounded-xl bg-card p-4 flex flex-col ${isNewest ? "border-2 border-foreground/70 ring-1 ring-accent/40 shadow-soft" : "border border-border"}`}
                                        data-testid={`vault-affidavit-${d.id}`}
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {isNewest && <Badge className="bg-foreground text-background text-[10px] font-mono uppercase tracking-wider">Newest · Start here</Badge>}
                                            <Badge variant="outline" className="bg-warning/10 border-warning/40 text-warning text-[10px]">
                                                <Stamp className="h-3 w-3 mr-1" /> Notarize first
                                            </Badge>
                                            <span className="text-[10px] font-mono text-muted-foreground">{dt}</span>
                                        </div>
                                        <p className="mt-2 font-serif text-lg leading-tight">{d.kind_label}</p>
                                        <p className="mt-0.5 text-xs text-muted-foreground truncate">{d.filename}</p>
                                        <p className="mt-0.5 text-[11px] text-muted-foreground">{Math.round((d.size || 0) / 1024)} KB · {d.mime}</p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 px-4 text-xs flex-1" data-testid={`vault-affidavit-download-${d.id}`}>
                                                <a href={dlHref(d.id)}><Download className="h-3.5 w-3.5 mr-1.5" /> Download &amp; print</a>
                                            </Button>
                                            <Button asChild variant="outline" className="rounded-full h-9 px-3 text-xs" data-testid={`vault-affidavit-notary-${d.id}`}>
                                                <a href="https://www.notarize.com" target="_blank" rel="noreferrer"><Stamp className="h-3.5 w-3.5 mr-1.5" /> Notary</a>
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-4 flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success" />
                            <span>Where to notarize: most <b>banks</b> notarize free for account-holders · <b>UPS Store / FedEx Office</b> ≈ $10 · <b>Notarize.com</b> online ≈ $25 (~15 min, Texas-legal).</span>
                        </div>
                    </div>
                );
            })()}

            {/* Letters, motions, and protests — pre-filled paperwork that does
                NOT require notarization. Just sign and mail certified. */}
            {(() => {
                const LETTER_KINDS = ["packet_tax_letter", "packet_25_25_motion", "packet_41_41_protest", "packet_cad_letter", "packet_quiet_title_petition", "packet_quiet_title_checklist", "packet_trla_intake"];
                const letters = docs
                    .filter(d => LETTER_KINDS.includes(d.kind))
                    .slice()
                    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                if (letters.length === 0) return null;
                const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                const dlHref = (id) => `${backendBase}/api/vault/documents/${id}/download`;
                return (
                    <div className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="vault-letters-section">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-muted-foreground" />
                            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Letters, motions &amp; protests</p>
                        </div>
                        <h3 className="mt-1 font-serif text-xl leading-tight">No notary needed — just sign &amp; mail certified.</h3>
                        <div className="mt-3 space-y-2">
                            {letters.map(d => (
                                <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 border border-border" data-testid={`vault-letter-${d.id}`}>
                                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{d.kind_label}</p>
                                        <p className="text-[11px] text-muted-foreground truncate">{d.filename}</p>
                                    </div>
                                    <Button asChild size="sm" variant="outline" className="rounded-full h-8 shrink-0">
                                        <a href={dlHref(d.id)} data-testid={`vault-letter-download-${d.id}`}><Download className="h-3.5 w-3.5" /></a>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Full list — grouped by topical section, newest-first within each.
                Sections only render if they have items.  */}
            {(() => {
                const TOPICS = [
                    {
                        id: "tax",
                        label: "Tax disputes & appraisal",
                        kinds: ["packet_tax_letter", "packet_25_25_motion", "packet_41_41_protest", "packet_cad_letter", "packet_prop_affidavit"],
                    },
                    {
                        id: "quiet_title",
                        label: "Quiet title & property fraud",
                        kinds: ["packet_quiet_title_petition", "packet_quiet_title_checklist"],
                    },
                    {
                        id: "affidavits",
                        label: "Sworn affidavits",
                        kinds: ["affidavit"],
                    },
                    {
                        id: "identity",
                        label: "Identity & residency",
                        kinds: ["government_id", "proof_of_address", "ssn_card", "birth_certificate"],
                    },
                    {
                        id: "financial",
                        label: "Financial",
                        kinds: ["tax_return", "w2", "bank_statement"],
                    },
                    {
                        id: "business",
                        label: "Business",
                        kinds: ["business_doc"],
                    },
                    {
                        id: "other",
                        label: "Other supporting docs",
                        kinds: ["other"],
                    },
                ];
                const matchedIds = new Set();
                const groupedSections = TOPICS.map(t => {
                    const items = docs
                        .filter(d => t.kinds.includes(d.kind))
                        .slice()
                        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                    items.forEach(it => matchedIds.add(it.id));
                    return { ...t, items };
                }).filter(s => s.items.length > 0);
                const orphans = docs.filter(d => !matchedIds.has(d.id));
                return (
                    <div className="mt-5">
                        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">All documents · {docs.length} · grouped by topic</p>
                        {docs.length === 0 ? (
                            <p className="mt-3 text-sm text-muted-foreground italic">No documents yet — upload at least a government ID to unlock most grant applications.</p>
                        ) : (
                            <div className="mt-3 space-y-5">
                                {groupedSections.map(section => (
                                    <div key={section.id} data-testid={`vault-topic-${section.id}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="h-1 w-6 rounded-full bg-foreground/40" />
                                            <p className="font-serif text-base">{section.label}</p>
                                            <Badge variant="outline" className="bg-secondary/60 border-border text-[10px] font-mono">{section.items.length}</Badge>
                                        </div>
                                        <div className="space-y-2">
                                            {section.items.map(d => (
                                                <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card" data-testid={`vault-doc-${d.id}`}>
                                                    <span className="h-9 w-9 rounded-lg bg-secondary inline-flex items-center justify-center shrink-0">
                                                        {d.mime?.startsWith("image/") ? <ImageIcon className="h-4 w-4 text-muted-foreground" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="text-sm font-medium truncate">{d.filename}</p>
                                                            <Badge variant="outline" className="bg-secondary/60 border-border text-xs font-normal">{d.kind_label}</Badge>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">{Math.round((d.size || 0) / 1024)} KB · {d.mime}</p>
                                                    </div>
                                                    <button onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive p-2" title="Remove" data-testid={`vault-doc-delete-${d.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                {orphans.length > 0 && (
                                    <div data-testid="vault-topic-misc">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="h-1 w-6 rounded-full bg-foreground/40" />
                                            <p className="font-serif text-base">Other</p>
                                        </div>
                                        <div className="space-y-2">
                                            {orphans.map(d => (
                                                <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                                    <p className="text-sm flex-1 truncate">{d.filename}</p>
                                                    <button onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive p-2"><Trash2 className="h-3.5 w-3.5" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success" />
                <span>Documents are stored encrypted and only attached to packets YOU sign. Delete anytime.</span>
            </div>
        </Card>
    );
}
